import { z } from 'zod';

const schema = z.object({
  VITE_API_URL: z.url(),
  VITE_POSTHOG_KEY: z.string().min(1),
  VITE_POSTHOG_HOST: z.url().optional(),
  VITE_SENTRY_DSN: z.url().optional(),
  VITE_SENTRY_AUTH_TOKEN: z.string().optional(),
  VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
  // socket.io server URL (ADR-0015 socket-first realtime). Optional/defaulted —
  // the realtime client is infra only and not connected anywhere yet.
  VITE_SOCKET_URL: z.string().optional(),
  VITE_RELEASE: z.string().optional(),
  // Set to 'true' to mock /auth/* so protected routes render without a backend.
  // Works in dev and in prod builds (QA, before the backend is deployed).
  VITE_MOCK_AUTH: z.string().optional(),
  // Set to 'true' to mock the whole backend (auth + feature APIs) with MSW.
  // Works in dev and in prod builds (QA, before the backend is deployed).
  VITE_MOCK_API: z.literal('true').optional(),
});

export type Env = z.infer<typeof schema>;

export function parseEnv(raw: Record<string, string | undefined>): Env {
  // Treat empty strings as undefined so optional fields accept blank .env values.
  const coerced = Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [k, v === '' ? undefined : v]),
  );
  const result = schema.safeParse(coerced);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`Invalid environment configuration:\n${issues}`);
  }
  return result.data;
}

export const env: Env = parseEnv(import.meta.env as Record<string, string | undefined>);
